"use client";

import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useMemo, useState } from "react";
import type { FUSportsEvent, FUSportsLivescoreEvent } from "fansunited-data-layer";
import { usePostDataOptional } from "@/views/post/context/PostDataContext";
import { useTenantSettings } from "@/lib/tenant/TenantSettingsProvider";
import { SITE_TYPE_BASIC } from "@/config/siteTypes";
import { computeMatchListRefKey, type MatchListRef } from "@/blocks/MatchList/config/refKey";
import { isSport, isMatchListMode, SPORT_TO_LIVESCORE_SLUG, type Sport } from "@/blocks/MatchList/config/sportRules";
import { toBookmakerOdds } from "@/features/odds/adapters";
import type { MatchOddsMap } from "@/features/odds/types";
import { CompactMatchGroup } from "./CompactMatchGroup";
import type { MatchListBlock as MatchListBlockData } from "@/payload-types";
import type { ResolvedListings } from "@/features/tv-listings/utils/resolveMatchListings";
import { useLocale } from "@/lib/labels/LabelsProvider";
interface Props {
  block: MatchListBlockData;
}
const stringOrNull = (v: unknown): string | null => typeof v === "string" && v.length > 0 ? v : null;
const stringListOrNull = (v: unknown): string[] | null => Array.isArray(v) ? v.filter((x): x is string => typeof x === "string" && x.length > 0) : null;

/** Adapt a FUSportsEvent → FUSportsLivescoreEvent (competitor shape + optional score). */
function toLivescoreEvent(e: FUSportsEvent): FUSportsLivescoreEvent {
  return {
    id: e.id,
    status: e.status,
    score: e.score,
    competitorOne: {
      id: e.competitorOneId,
      type: "team"
    },
    competitorTwo: {
      id: e.competitorTwoId,
      type: "team"
    },
    startTime: e.startTime ?? "",
    timing: e.timing
  };
}
export function MatchListBlock(t0) {
  const $ = _c(51);
  const {
    block
  } = t0;
  const {
    siteType
  } = useTenantSettings();
  const postData = usePostDataOptional();
  let t1;
  bb0: {
    if (!isSport(block.sport) || !isMatchListMode(block.mode)) {
      t1 = null;
      break bb0;
    }
    let t2;
    if ($[0] !== block.competitionId || $[1] !== block.competitionIds || $[2] !== block.defaultMarket || $[3] !== block.entityId || $[4] !== block.fromDate || $[5] !== block.limit || $[6] !== block.matchIds || $[7] !== block.mode || $[8] !== block.roundId || $[9] !== block.seasonId || $[10] !== block.showOdds || $[11] !== block.sport || $[12] !== block.toDate) {
      const ref = {
        sport: block.sport,
        mode: block.mode,
        entityId: stringOrNull(block.entityId),
        competitionId: stringOrNull(block.competitionId),
        competitionIds: stringListOrNull(block.competitionIds),
        seasonId: stringOrNull(block.seasonId),
        roundId: stringOrNull(block.roundId),
        fromDate: stringOrNull(block.fromDate),
        toDate: stringOrNull(block.toDate),
        matchIds: stringListOrNull(block.matchIds),
        limit: typeof block.limit === "number" ? block.limit : null,
        showOdds: block.showOdds == null ? null : Boolean(block.showOdds),
        defaultMarket: stringOrNull(block.defaultMarket)
      };
      t2 = computeMatchListRefKey(ref);
      $[0] = block.competitionId;
      $[1] = block.competitionIds;
      $[2] = block.defaultMarket;
      $[3] = block.entityId;
      $[4] = block.fromDate;
      $[5] = block.limit;
      $[6] = block.matchIds;
      $[7] = block.mode;
      $[8] = block.roundId;
      $[9] = block.seasonId;
      $[10] = block.showOdds;
      $[11] = block.sport;
      $[12] = block.toDate;
      $[13] = t2;
    } else {
      t2 = $[13];
    }
    t1 = t2;
  }
  const refKey = t1;
  let t2;
  if ($[14] !== postData || $[15] !== refKey) {
    t2 = refKey ? postData?.getMatchListData(refKey) : undefined;
    $[14] = postData;
    $[15] = refKey;
    $[16] = t2;
  } else {
    t2 = $[16];
  }
  const payload = t2;
  const sport = isSport(block.sport) ? block.sport as Sport : null;
  let t3;
  bb1: {
    if (!payload || !sport) {
      let t4;
      if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
        t4 = [];
        $[17] = t4;
      } else {
        t4 = $[17];
      }
      t3 = t4;
      break bb1;
    }
    let out;
    if ($[18] !== payload.events) {
      out = [];
      for (const event of payload.events) {
        const last = out[out.length - 1];
        if (last && last.competitionId === event.competitionId) {
          last.events.push(toLivescoreEvent(event));
        } else {
          out.push({
            competitionId: event.competitionId,
            events: [toLivescoreEvent(event)]
          });
        }
      }
      $[18] = payload.events;
      $[19] = out;
    } else {
      out = $[19];
    }
    t3 = out;
  }
  const groups = t3;
  const locale = useLocale();
  let t4;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {};
    $[20] = t4;
  } else {
    t4 = $[20];
  }
  const [tvByMatch, setTvByMatch] = useState(t4);
  let t5;
  if ($[21] !== payload) {
    t5 = payload ? payload.events.map(_temp).filter(Boolean) : [];
    $[21] = payload;
    $[22] = t5;
  } else {
    t5 = $[22];
  }
  const matchIds = t5;
  let t6;
  if ($[23] !== locale || $[24] !== matchIds) {
    t6 = () => {
      if (matchIds.length === 0) {
        setTvByMatch({});
        return;
      }
      let cancelled = false;
      fetch("/api/match-tv-listings/by-matches", {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({
          matches: matchIds,
          locale
        })
      }).then(_temp2).then(data => {
        if (cancelled) {
          return;
        }
        setTvByMatch(data || {});
      }).catch(_temp3);
      return () => {
        cancelled = true;
      };
    };
    $[23] = locale;
    $[24] = matchIds;
    $[25] = t6;
  } else {
    t6 = $[25];
  }
  let t7;
  if ($[26] !== matchIds) {
    t7 = matchIds.join(",");
    $[26] = matchIds;
    $[27] = t7;
  } else {
    t7 = $[27];
  }
  let t8;
  if ($[28] !== locale || $[29] !== t7) {
    t8 = [t7, locale];
    $[28] = locale;
    $[29] = t7;
    $[30] = t8;
  } else {
    t8 = $[30];
  }
  useEffect(t6, t8);
  let t9;
  if ($[31] !== tvByMatch) {
    t9 = Object.keys(tvByMatch);
    $[31] = tvByMatch;
    $[32] = t9;
  } else {
    t9 = $[32];
  }
  const anyTvInBlock = t9.length > 0;
  let t10;
  if ($[33] !== payload) {
    bb2: {
      const m = new Map();
      if (!payload?.oddsByMatchId) {
        t10 = m;
        break bb2;
      }
      for (const [matchId, operators] of Object.entries(payload.oddsByMatchId)) {
        const converted = operators.map(toBookmakerOdds).filter(_temp4);
        if (converted.length > 0) {
          m.set(matchId, converted);
        }
      }
      t10 = m;
    }
    $[33] = payload;
    $[34] = t10;
  } else {
    t10 = $[34];
  }
  const oddsMap = t10;
  if (siteType === SITE_TYPE_BASIC) {
    return null;
  }
  if (!postData) {
    if (process.env.NODE_ENV !== "production") {
      console.warn("[MatchListBlock] used outside PostDataProvider \u2014 rendering nothing");
    }
    return null;
  }
  if (!refKey || !sport) {
    if (process.env.NODE_ENV !== "production") {
      console.warn("[MatchListBlock] invalid sport/mode; rendering nothing", {
        sport: block.sport,
        mode: block.mode
      });
    }
    return null;
  }
  if (!payload) {
    if (process.env.NODE_ENV !== "production") {
      console.warn("[MatchListBlock] no pre-fetched data for refKey", refKey);
    }
    return null;
  }
  if (payload.events.length === 0) {
    if (process.env.NODE_ENV !== "production") {
      console.warn("[MatchListBlock] pre-fetch returned zero events", {
        refKey
      });
    }
    return null;
  }
  let t11;
  if ($[35] !== block.title) {
    t11 = typeof block.title === "string" ? block.title.trim() : "";
    $[35] = block.title;
    $[36] = t11;
  } else {
    t11 = $[36];
  }
  const title = t11;
  const livescoreSport = SPORT_TO_LIVESCORE_SLUG[sport];
  const showOdds = block.showOdds !== false;
  let t12;
  if ($[37] !== title) {
    t12 = title && <h3 className="mb-3 text-base font-semibold">{title}</h3>;
    $[37] = title;
    $[38] = t12;
  } else {
    t12 = $[38];
  }
  let t13;
  if ($[39] !== anyTvInBlock || $[40] !== groups || $[41] !== livescoreSport || $[42] !== oddsMap || $[43] !== payload.entities || $[44] !== payload.roundLabel || $[45] !== showOdds || $[46] !== tvByMatch) {
    t13 = groups.map((group, i) => <CompactMatchGroup key={`${group.competitionId}:${i}`} competitionId={group.competitionId} events={group.events} entities={payload.entities} sport={livescoreSport} oddsMap={oddsMap} showOdds={showOdds} roundLabel={payload.roundLabel} tvByMatch={tvByMatch} reserveTvSlot={anyTvInBlock} />);
    $[39] = anyTvInBlock;
    $[40] = groups;
    $[41] = livescoreSport;
    $[42] = oddsMap;
    $[43] = payload.entities;
    $[44] = payload.roundLabel;
    $[45] = showOdds;
    $[46] = tvByMatch;
    $[47] = t13;
  } else {
    t13 = $[47];
  }
  let t14;
  if ($[48] !== t12 || $[49] !== t13) {
    t14 = <div data-element="match-list-block" className="my-6 not-prose">{t12}{t13}</div>;
    $[48] = t12;
    $[49] = t13;
    $[50] = t14;
  } else {
    t14 = $[50];
  }
  return t14;
}
function _temp4(b) {
  return b !== null;
}
function _temp3() {}
function _temp2(r) {
  return r.ok ? r.json() : {};
}
function _temp(e) {
  return e.id;
}